import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../../AdminPanel/DataDisplay/useDisplayConfigState';
import { AffinityFormValues, affinityConfigSchema, getDefaultAffinityConfig } from './affinityConfig.schema';
import { useAffinityConfigActions } from './useAffinityConfigActions';
import { useIntegrationActions } from '../hooks/useIntegrationActions';
import { trackEvent } from '../../../util/datadogTracking';
import { AppName } from '../../../data-models/integrations-app.data-model';

export function useAffinityConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { saveConfig, isSubmitting } = useAffinityConfigActions();
  const { refetchIntegrations } = useIntegrationActions();

  const schema = affinityConfigSchema();
  const methods = useForm<AffinityFormValues>({
    resolver: yupResolver(schema as ObjectSchema<AffinityFormValues>),
    defaultValues: getDefaultAffinityConfig(),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors },
    reset,
    trigger,
  } = methods;

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Affinity Config', errors);
      return;
    }
    // Track whether the connect itself succeeded so a later failure (e.g. the
    // post-connect refetch) can't emit a second, contradictory event.
    let connected = false;
    try {
      const result = await saveConfig(getValues());
      connected = true;
      trackEvent('integration.connected', { provider: AppName.Affinity, success: true });
      setSuccessMessage(result.message);
      setErrorMessage('');
      setEditStatus('saved');
      reset(getDefaultAffinityConfig());
      await refetchIntegrations();
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setErrorMessage(axiosError?.response?.data?.message || 'Failed to configure Affinity connection');
      setSuccessMessage('');
      if (!connected) {
        trackEvent('integration.connected', { provider: AppName.Affinity, success: false });
      }
    }
  }, [trigger, saveConfig, getValues, errors, reset, refetchIntegrations]);

  const onReset = useCallback(() => {
    reset(getDefaultAffinityConfig());
    setEditStatus('initial');
    setSuccessMessage('');
    setErrorMessage('');
  }, [reset]);

  return {
    editStatus,
    isLoading: isSubmitting,
    successMessage,
    errorMessage,
    onSave,
    onReset,
    methods,
  };
}
